import { useState, useEffect } from 'react';
import {
  Key, Shield, Link2, Eye, EyeOff, CheckCircle, AlertCircle,
  ExternalLink, Loader2, Settings, ArrowRight, Info, Copy, Check
} from 'lucide-react';
import api from '@/api';

// Slack logo SVG component
function SlackLogo({ className = "w-6 h-6" }) {
  return (
    <svg className={className} viewBox="0 0 127 127" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M27.2 80c0 7.3-5.9 13.2-13.2 13.2C6.7 93.2.8 87.3.8 80c0-7.3 5.9-13.2 13.2-13.2h13.2V80zm6.6 0c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2v33c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V80z" fill="#E01E5A" />
      <path d="M47 27c-7.3 0-13.2-5.9-13.2-13.2C33.8 6.5 39.7.6 47 .6c7.3 0 13.2 5.9 13.2 13.2V27H47zm0 6.7c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H13.9C6.6 60.1.7 54.2.7 46.9c0-7.3 5.9-13.2 13.2-13.2H47z" fill="#36C5F0" />
      <path d="M99.9 46.9c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H99.9V46.9zm-6.6 0c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V13.8C66.9 6.5 72.8.6 80.1.6c7.3 0 13.2 5.9 13.2 13.2v33.1z" fill="#2EB67D" />
      <path d="M80.1 99.8c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V99.8h13.2zm0-6.6c-7.3 0-13.2-5.9-13.2-13.2 0-7.3 5.9-13.2 13.2-13.2h33.1c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H80.1z" fill="#ECB22E" />
    </svg>
  );
}

// Input field with visibility toggle for secrets
function SecretInput({
  label,
  value,
  onChange,
  placeholder,
  helpText,
  error,
  icon: Icon = Key
}) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          <Icon className="w-4 h-4" />
        </div>
        <input
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full pl-10 pr-10 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono ${error ? 'border-destructive/50 bg-destructive/10' : 'border-border'
            }`}
        />
        <button
          type="button"
          onClick={() => setVisible(!visible)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {error && (
        <p className="text-xs text-red-600">{error}</p>
      )}
      {helpText && !error && (
        <p className="text-xs text-gray-500">{helpText}</p>
      )}
    </div>
  );
}

// Copy button component
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1 text-gray-400 hover:text-gray-600 rounded"
      title="Copy to clipboard"
    >
      {copied ? (
        <Check className="w-4 h-4 text-success" />
      ) : (
        <Copy className="w-4 h-4" />
      )}
    </button>
  );
}

export default function SlackCredentialsConfig({
  onConfigured
}) {
  // State
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [signingSecret, setSigningSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [credentialsStatus, setCredentialsStatus] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Check current credentials status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await api.get('/slack/credentials/status');
        setCredentialsStatus(response.data);

        // If already configured, notify parent
        if (response.data.configured && onConfigured) {
          onConfigured(response.data);
        }
      } catch (err) {
        console.error('Failed to check credentials status:', err);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkStatus();
  }, [onConfigured]);

  // Validate inputs
  const validate = () => {
    const errors = {};

    if (!clientId.trim()) {
      errors.clientId = 'Client ID is required';
    } else if (clientId.length < 10) {
      errors.clientId = 'Client ID seems too short';
    }

    if (!clientSecret.trim()) {
      errors.clientSecret = 'Client Secret is required';
    } else if (clientSecret.length < 10) {
      errors.clientSecret = 'Client Secret seems too short';
    }

    if (!signingSecret.trim()) {
      errors.signingSecret = 'Signing Secret is required';
    } else if (signingSecret.length < 10) {
      errors.signingSecret = 'Signing Secret seems too short';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validate()) return;

    setLoading(true);
    setError(null);

    try {
      const payload = {
        client_id: clientId.trim(),
        client_secret: clientSecret.trim(),
        signing_secret: signingSecret.trim()
      };

      if (redirectUri.trim()) {
        payload.redirect_uri = redirectUri.trim();
      }

      await api.post('/slack/credentials', payload);

      setSuccess(true);

      // Notify parent after a brief delay
      setTimeout(() => {
        if (onConfigured) {
          onConfigured({ configured: true, source: 'database' });
        }
      }, 1500);

    } catch (err) {
      console.error('Failed to save credentials:', err);
      setError(err.response?.data?.detail || 'Failed to save credentials. Please check your inputs.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (checkingStatus) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  // Already configured state
  if (credentialsStatus?.configured && credentialsStatus.source === 'environment') {
    return (
      <div className="bg-success/10 border border-success/30 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-success/20 rounded-lg">
            <CheckCircle className="w-6 h-6 text-success" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-success">Credentials Configured via Environment</h3>
            <p className="text-sm text-success/80 mt-1">
              Slack credentials are configured through environment variables. You're all set to connect!
            </p>
            {onConfigured && (
              <button
                onClick={() => onConfigured(credentialsStatus)}
                className="mt-4 px-4 py-2 bg-success hover:bg-success/90 text-success-foreground font-medium rounded-lg flex items-center gap-2"
              >
                Continue to Connect
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="bg-success/10 border border-success/30 rounded-xl p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-success/20 rounded-full mb-4">
          <CheckCircle className="w-8 h-8 text-success" />
        </div>
        <h3 className="text-xl font-bold text-success mb-2">Credentials Saved!</h3>
        <p className="text-success/80 mb-4">
          Your Slack app credentials have been securely saved. Redirecting to connect...
        </p>
        <Loader2 className="w-6 h-6 text-success animate-spin mx-auto" />
      </div>
    );
  }

  // Get the current app URL for redirect URI suggestion (matches GitHub pattern)
  const suggestedRedirectUri = 'https://vedaya-api.gauranshtandon.workers.dev/api/connect/callback/slack';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-gradient-to-br from-primary/20 to-primary/10 rounded-xl">
          <SlackLogo className="w-10 h-10" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Configure Slack App</h2>
          <p className="text-sm text-gray-500">Enter your Slack app credentials to enable OAuth</p>
        </div>
      </div>

      {/* Error alert */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
          <div>
            <p className="text-sm text-destructive">{error}</p>
          </div>
        </div>
      )}

      {/* Instructions panel */}
      <div className="bg-info/10 border border-info/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-info flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-info">Where to find these credentials</h4>
            <ol className="mt-2 text-sm text-info/80 space-y-1 list-decimal list-inside">
              <li>Go to <a href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer" className="underline hover:text-info">api.slack.com/apps</a></li>
              <li>Create a new app or select an existing one</li>
              <li>Find <strong>Client ID</strong> and <strong>Client Secret</strong> under "Basic Information"</li>
              <li>Find <strong>Signing Secret</strong> under "Basic Information" → "App Credentials"</li>
              <li>Add your Redirect URL under "OAuth & Permissions"</li>
            </ol>
            <a
              href="https://api.slack.com/apps"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-info hover:text-info/80"
            >
              Open Slack API Dashboard
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>

      {/* Credential inputs */}
      <div className="space-y-4">
        <SecretInput
          label="Client ID"
          value={clientId}
          onChange={setClientId}
          placeholder="1234567890.1234567890123"
          helpText="Found in Basic Information → App Credentials"
          error={validationErrors.clientId}
          icon={Key}
        />

        <SecretInput
          label="Client Secret"
          value={clientSecret}
          onChange={setClientSecret}
          placeholder="abcdef1234567890abcdef1234567890"
          helpText="Found in Basic Information → App Credentials"
          error={validationErrors.clientSecret}
          icon={Shield}
        />

        <SecretInput
          label="Signing Secret"
          value={signingSecret}
          onChange={setSigningSecret}
          placeholder="abcdef1234567890abcdef1234567890"
          helpText="Found in Basic Information → App Credentials (scroll down)"
          error={validationErrors.signingSecret}
          icon={Shield}
        />

        {/* Advanced options toggle */}
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          <Settings className="w-4 h-4" />
          {showAdvanced ? 'Hide' : 'Show'} Advanced Options
        </button>

        {/* Advanced options */}
        {showAdvanced && (
          <div className="pl-4 border-l-2 border-gray-200 space-y-4">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Redirect URI (Optional)
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Link2 className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  value={redirectUri}
                  onChange={(e) => setRedirectUri(e.target.value)}
                  placeholder={suggestedRedirectUri}
                  className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono"
                />
              </div>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-xs text-gray-500">Suggested:</p>
                <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono text-gray-700">
                  {suggestedRedirectUri}
                </code>
                <CopyButton text={suggestedRedirectUri} />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Add this URL to "OAuth & Permissions" → "Redirect URLs" in your Slack app settings
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Required OAuth Scopes info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Required OAuth Scopes</h4>
        <p className="text-xs text-gray-600 mb-2">
          Make sure your Slack app has these Bot Token Scopes enabled:
        </p>
        <div className="flex flex-wrap gap-1.5">
          {['channels:read', 'channels:history', 'groups:read', 'groups:history', 'users:read', 'reactions:read'].map(scope => (
            <span
              key={scope}
              className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs font-mono rounded"
            >
              {scope}
            </span>
          ))}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-end pt-4 border-t border-border">
        <button
          onClick={handleSave}
          disabled={loading || !clientId || !clientSecret || !signingSecret}
          className="px-6 py-2.5 bg-primary hover:bg-primary/90 disabled:bg-primary/50 disabled:cursor-not-allowed text-primary-foreground font-medium rounded-lg flex items-center gap-2 transition-colors"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              Save & Continue
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
